<template>
  <div class="card-info-table">
    <div class="info-table-container">
      <!-- ⚠️ 外面套一层 .info-table-radius：
           border-collapse: collapse 的表格自身不支持 border-radius（浏览器会忽略），
           所以把「外框 + 圆角 + 裁切」交给这一层，表格内部只画格子线。 -->
      <div class="info-table-radius">
        <table class="info-table" ref="infoTable">
          <tbody>
            <tr
              v-for="(row, rowIndex) in organizedData"
              :key="rowIndex"
              class="info-row"
              :data-cols="row.length * 2"
            >
              <template v-for="(item, colIndex) in row">
                <td
                  :key="'label-' + colIndex"
                  class="label-cell"
                  :class="{ 'is-empty': !item }"
                >
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    :content="item ? item.label : ''"
                    :enterable="true"
                    :hide-after="0"
                    :open-delay="200"
                    :disabled="!item || !item.label"
                  >
                    <span class="cell-text">{{ item ? item.label : '' }}</span>
                  </el-tooltip>
                </td>
                <td
                  :key="'value-' + colIndex"
                  class="value-cell"
                  :class="{ 'is-empty': !item }"
                >
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    :content="item ? item.value : ''"
                    :enterable="true"
                    :hide-after="0"
                    :open-delay="200"
                    :disabled="!item || !item.value"
                  >
                    <!-- ✅ valueClass：调用方可给某一项单独传类名，用来做值的高亮（如「评审进度」） -->
                    <span class="cell-text" :class="item ? (item.valueClass || '') : ''">
                      {{ item ? item.value : emptyText }}
                    </span>
                  </el-tooltip>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

export default {
  name: 'CardInfoTable',
  props: {
    /** 数据源 */
    data: {
      type: Array,
      required: true,
      default: () => []
    },

    /** 每行显示几组（1 / 2 / 3） */
    itemsPerRow: {
      type: Number,
      default: 2,
      validator: v => [1, 2, 3].includes(v)
    },

    /** 标签列宽度 */
    labelWidth: {
      type: String,
      default: '20%'
    },

    /** 行高 */
    rowHeight: {
      type: String,
      default: '40px'
    },

    /** 字体大小 */
    fontSize: {
      type: String,
      default: '13px'
    },

    /** 标签背景色 */
    labelBgColor: {
      type: String,
      default: 'rgba(37, 100, 207, 0.08)'
    },

    /** 边框颜色 */
    borderColor: {
      type: String,
      default: '#dcdfe6'
    },

    /** 空值文本 */
    emptyText: {
      type: String,
      default: '无'
    },

    /** 导出文件名 */
    exportFileName: {
      type: String,
      default: '信息表格'
    }
  },

  computed: {
    organizedData() {
      const items = [...this.data]
      const rows = []
      const cols = this.itemsPerRow

      const totalRows = Math.ceil(items.length / cols)

      for (let r = 0; r < totalRows; r++) {
        const row = []
        for (let c = 0; c < cols; c++) {
          const index = r * cols + c
          row.push(index < items.length ? items[index] : null)
        }
        rows.push(row)
      }

      return rows
    }
  },

  methods: {
    /** 导出 Excel */
    handleExport() {
      const table = this.$refs.infoTable
      if (!table) {
        this.$message && this.$message.warning('表格未渲染')
        return
      }
      const wb = XLSX.utils.table_to_book(table, { raw: true })
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      })
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          this.exportFileName + '.xlsx'
        )
      } catch (e) {
        console.log(e, wbout)
      }
    }
  }
}
</script>

<style scoped lang="scss">
/* ✅ 表格圆角：全站 CardInfoTable 统一用它，想改大小只动这一个变量 */
$table-radius: 6px;

.card-info-table {
  width: 100%;
  height: 100%;
  font-family: 'Microsoft YaHei', 微软雅黑, sans-serif;
}

.info-table-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 5px;
  padding-top: 5px;
  box-sizing: border-box;
}

/* ✅ 圆角 + 外框都在这一层做：
   1) border-collapse: collapse 的 table 本身不支持 border-radius（浏览器会忽略），
      所以外框和圆角交给这一层；
   2) overflow:hidden 负责把「标签列的淡蓝底色」和格子线按圆角裁掉。 */
.info-table-radius {
  width: 100%;
  border: 1px solid v-bind(borderColor);
  border-radius: $table-radius;
  overflow: hidden;
  box-sizing: border-box;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border: none; /* 外框移到 .info-table-radius 上，这里只画内部格子线 */
  font-size: v-bind(fontSize);

  .info-row {
    height: v-bind(rowHeight);

    &:hover {
      background-color: rgba(37, 100, 207, 0.05);
    }
  }

  .label-cell {
    width: v-bind(labelWidth);
    // padding: 0 10px;
    border-right: 1px solid v-bind(borderColor);
    border-bottom: 1px solid v-bind(borderColor);
    background-color: v-bind(labelBgColor);
    color: #606266;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    .cell-text {
      display: block;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .value-cell {
    padding: 0 10px;
    border-right: 1px solid v-bind(borderColor);
    border-bottom: 1px solid v-bind(borderColor);
    color: #4D4F53;
    text-align: left;

    .cell-text {
      display: block;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  /* 最后一行去底边框 */
  .info-row:last-child .label-cell,
  .info-row:last-child .value-cell {
    border-bottom: none;
  }

  /* 每行最后一个单元格去右边框：右边的外框改由 .info-table-radius 画，避免两条线贴一起 */
  .info-row .value-cell:last-child {
    border-right: none;
  }
}

/* ==============================
   手机端：2 栏（flex + ellipsis）
   ============================== */
@media (max-width: 768px) {
  /* 手机端每行自己带边框（原本就没有整表外框），这里去掉外层描边，保持原来的样子 */
  .info-table-radius {
    border: none;
  }

  .info-table {
    display: block;
    border: none;
    overflow: hidden;
  }

  .info-table tbody {
    display: block;
  }

  .info-table-container {
    padding: 0px;
    padding-top: 0px;
  }

  .info-row {
    display: grid;
    border: 1px solid #dcdfe6;
    border-bottom: none;
    align-items: stretch;
  }

  /* 一行 1 组信息：2 个 td（label + value） */
  .info-row[data-cols="2"] {
    grid-template-columns: 25% 75%;
  }

  /* 一行 2 组信息：4 个 td */
  .info-row[data-cols="4"] {
    grid-template-columns: 20% 40% 18% 22%;
  }

  /* 一行 3 组信息：6 个 td */
  .info-row[data-cols="6"] {
    grid-template-columns: 15% 25% 15% 20% 10% 15%;
  }

  .info-row:last-child {
    border-bottom: 1px solid #dcdfe6;
  }

  /* ✅ label */
  .label-cell {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100% !important;
    border-right: none;
    border-bottom: none;
    box-sizing: border-box;
    padding: 0 6px;

    /* ✅ 关键 */
    min-width: 0;
  }

  /* ✅ value */
  .value-cell {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    width: 100% !important;
    border-right: none;
    border-bottom: none;
    box-sizing: border-box;
    padding: 0 6px;

    /* ✅ 关键 */
    min-width: 0;
  }

  /* ✅ cell-text 省略号 */
  .cell-text {
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>